/**
 * The public share page.
 *
 * The only screen served to someone with no account, so it deliberately shows
 * nothing but the photos: no group name, no member list, no uploader, no
 * navigation into the rest of the app. What the API withholds, the UI does not
 * reintroduce.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { linkKeyFromLocation } from "./linkKey.js";
import { openSharedMedia, releaseSharedMedia } from "./shareMedia.js";
import { imageAccept } from "../../lib/imageSupport.js";
import "./SharePage.css";

interface ShareItem {
  id: string;
  name: string;
  kind: "photo" | "video";
  width: number | null;
  height: number | null;
  takenAt: string;
}

/** Survives a reload so a password is not re-typed on every refresh. */
const sessionKey = (token: string) => `fc-share-${token}`;

export function SharePage() {
  const { token } = useParams({ from: "/s/$token" });

  const [session, setSession] = useState<string | undefined>(
    () => sessionStorage.getItem(sessionKey(token)) ?? undefined,
  );
  const [locked, setLocked] = useState(false);
  const [items, setItems] = useState<ShareItem[]>([]);
  const [allowDownload, setAllowDownload] = useState(false);
  const [urls, setUrls] = useState<Map<string, string>>(new Map());

  /*
   * Decrypted thumbnails are blobs this page made, and a blob outlives the
   * component that created it unless it is revoked. Forty of them held for
   * the life of a tab is a real amount of memory on a phone.
   */
  useEffect(
    () => () => {
      releaseSharedMedia(urls.values());
    },
    [urls],
  );
  const [status, setStatus] = useState<"loading" | "ready" | "gone">("loading");
  /**
   * The link arrived without the part that opens it.
   *
   * Its own state because it is a different problem from an expired link and
   * needs a different sentence: nothing is wrong with the link, the address is
   * incomplete. Chat clients and URL shorteners drop fragments, and somebody
   * who retypes a link from a screenshot will drop it too.
   */
  const [keyMissing, setKeyMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const load = useCallback(
    async (withSession: string | undefined) => {
      try {
        const view = await trpcClient.share.view.query({
          token,
          session: withSession,
        });

        if (view.locked) {
          setLocked(true);
          setStatus("ready");
          return;
        }

        setLocked(false);
        setAllowDownload(view.allowDownload);
        setItems((view.items ?? []) as ShareItem[]);
        setStatus("ready");

        const accept = await imageAccept();

        /*
         * The key lives in this page's own address and was never sent to
         * reach it. Read once here rather than per photograph, because a
         * link with no key is a state to report, not forty failures.
         */
        const linkKey = linkKeyFromLocation(window.location.hash);

        const resolved = await Promise.all(
          (view.items ?? []).map(async (item) => {
            const media = await trpcClient.share.mediaUrl
              .query({
                token,
                fileId: item.id,
                variant: "thumb",
                session: withSession,
                accept,
              })
              .catch(() => null);

            if (!media?.url) return [item.id, null] as const;

            try {
              return [
                item.id,
                await openSharedMedia(
                  media.url,
                  media.sealedFileKey,
                  linkKey,
                ),
              ] as const;
            } catch {
              /* One unreadable photograph, not a broken page. */
              return [item.id, null] as const;
            }
          }),
        );

        /*
         * Said once, plainly, when the link cannot possibly work. Every
         * photograph failing individually would look like forty faults.
         */
        if (!linkKey && (view.items ?? []).length > 0) {
          setKeyMissing(
            resolved.every(([, url]) => url === null),
          );
        }
        setUrls(
          new Map(
            resolved.filter((entry): entry is [string, string] =>
              Boolean(entry[1]),
            ),
          ),
        );
      } catch {
        // Revoked, expired and never-existed are one answer by design.
        setStatus("gone");
      }
    },
    [token],
  );

  useEffect(() => {
    void load(session);
  }, [load, session]);

  async function unlock(event: React.FormEvent) {
    event.preventDefault();
    setUnlocking(true);
    setError(null);

    try {
      const result = await trpcClient.share.unlock.mutate({ token, password });
      sessionStorage.setItem(sessionKey(token), result.session);
      setSession(result.session);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setUnlocking(false);
    }
  }

  const openItem = useMemo(
    () => (lightbox !== null ? items[lightbox] : undefined),
    [lightbox, items],
  );

  useEffect(() => {
    if (lightbox === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowLeft")
        setLightbox((i) => (i === null ? null : Math.max(0, i - 1)));
      if (event.key === "ArrowRight")
        setLightbox((i) =>
          i === null ? null : Math.min(items.length - 1, i + 1),
        );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, items.length]);

  if (status === "gone") {
    return (
      <div className="fc-sharepage fc-sharepage--centred">
        <EmptyState
          icon={<LinkIcon />}
          title="This link is no longer available"
          description="It may have expired, or whoever shared it may have turned it off. Ask them for a new one."
        />
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="fc-sharepage">
        <div className="fc-sharepage__grid">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} height="100%" radius="var(--fc-radius-md)" />
          ))}
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="fc-sharepage fc-sharepage--centred">
        <form className="fc-sharepage__lock" onSubmit={unlock}>
          <LockIcon />
          <h1>These photos are password protected</h1>
          <p>Enter the password you were given to view them.</p>

          {error && (
            <div className="fc-sharepage__error" role="alert">
              {error}
            </div>
          )}

          <Field label="Password" htmlFor="share-password">
            <Input
              id="share-password"
              type="password"
              autoFocus
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" variant="primary" loading={unlocking}>
            View photos
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="fc-sharepage">
      <header className="fc-sharepage__header">
        <h1>
          {items.length} shared {items.length === 1 ? "photo" : "photos"}
        </h1>
        <p>Shared with you via MyFriends.Cloud</p>
      </header>

      {/*
        The address is incomplete, which is not the same as the link being
        dead — and the difference matters, because one of them is fixable by
        asking for the link again rather than for a new one.
      */}
      {keyMissing && (
        <div className="fc-sharepage__error" role="alert">
          This link is missing the part after the <code>#</code>, which is what
          unlocks the photos. It is dropped by some chat apps and by link
          shorteners. Ask for the full link again and open it directly.
        </div>
      )}

      <div className="fc-sharepage__grid">
        {items.map((item, index) => {
          const url = urls.get(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className="fc-sharepage__tile"
              onClick={() => setLightbox(index)}
              aria-label={`View ${item.name}`}
            >
              {url ? (
                <img src={url} alt="" loading="lazy" decoding="async" />
              ) : (
                <Skeleton height="100%" />
              )}
            </button>
          );
        })}
      </div>

      {openItem && (
        <ShareLightbox
          token={token}
          session={session}
          item={openItem}
          index={lightbox!}
          total={items.length}
          allowDownload={allowDownload}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}

      <footer className="fc-sharepage__footer">
        Photos are shown at preview quality and carry no camera or location
        data.
      </footer>
    </div>
  );
}

function ShareLightbox({
  token,
  session,
  item,
  index,
  total,
  allowDownload,
  onClose,
  onNavigate,
}: {
  token: string;
  session: string | undefined;
  item: ShareItem;
  index: number;
  total: number;
  allowDownload: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);

    void (async () => {
      const media = await trpcClient.share.mediaUrl
        .query({
          token,
          fileId: item.id,
          variant: "preview",
          session,
          accept: await imageAccept(),
        })
        .catch(() => null);

      if (!media?.url) return;

      const opened = await openSharedMedia(
        media.url,
        media.sealedFileKey,
        linkKeyFromLocation(window.location.hash),
      ).catch(() => null);

      /*
       * Revoked rather than left behind if the viewer moved on while this
       * was decrypting — a blob nobody will ever show is a leak the length
       * of the session.
       */
      if (cancelled) {
        if (opened) releaseSharedMedia([opened]);
        return;
      }
      setUrl(opened);
    })();

    return () => {
      cancelled = true;
    };
  }, [token, session, item.id]);

  async function download() {
    const media = await trpcClient.share.mediaUrl
      .query({
        token,
        fileId: item.id,
        variant: "preview",
        session,
        download: true,
      })
      .catch(() => null);
    if (!media?.url) return;

    /*
     * Navigating straight to the signed URL would save the ciphertext.
     *
     * That is what this did, and under encryption it hands somebody a file
     * their computer cannot open and no error to explain it. The bytes have
     * to be decrypted here, on the only machine holding the link key, and
     * offered as a blob.
     */
    let objectUrl: string;
    try {
      objectUrl = await openSharedMedia(
        media.url,
        media.sealedFileKey,
        linkKeyFromLocation(window.location.hash),
      );
    } catch {
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = item.name;
    anchor.click();

    /* After the click, so the browser has taken its copy of the blob. */
    if (objectUrl !== media.url) {
      setTimeout(() => releaseSharedMedia([objectUrl]), 60_000);
    }
  }

  return (
    <div
      className="fc-sharepage__viewer"
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
    >
      <header>
        <button type="button" onClick={onClose} aria-label="Close">
          ✕
        </button>
        <span className="fc-tabular">
          {index + 1} / {total}
        </span>
        {allowDownload && (
          <Button variant="ghost" size="sm" onClick={() => void download()}>
            Download
          </Button>
        )}
      </header>

      <div className="fc-sharepage__stage" onClick={onClose}>
        {url ? (
          <img
            src={url}
            alt={item.name}
            onClick={(event) => event.stopPropagation()}
          />
        ) : (
          <span className="fc-sharepage__loading">Loading…</span>
        )}
      </div>

      {index > 0 && (
        <button
          type="button"
          className="fc-sharepage__nav fc-sharepage__nav--prev"
          onClick={() => onNavigate(index - 1)}
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      {index < total - 1 && (
        <button
          type="button"
          className="fc-sharepage__nav fc-sharepage__nav--next"
          onClick={() => onNavigate(index + 1)}
          aria-label="Next"
        >
          ›
        </button>
      )}
    </div>
  );
}

const LinkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path
      d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"
      strokeLinecap="round"
    />
    <path
      d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"
      strokeLinecap="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="fc-sharepage__lock-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" strokeLinecap="round" />
  </svg>
);
